import gql from 'graphql-tag';
import get from 'lodash/get';
import { compose, graphql } from 'react-apollo';
import { parse } from 'src/_shared/services/utils.js';
import uuid from 'uuid/v4';
import {
	createUserGroup,
	listUserGroups,
	updateUserGroup,
} from '../../graphql/custom/userGroups';

export const withListUserGroup = (Component) => {
	return compose(
		graphql(gql(listUserGroups), {
			options: (props) => ({
				context: {
					headers: {
						'x-frame-options': 'deny', // This header will reach the server
					},
				},
				variables: {
					filter: {
						companyId: { eq: props.currentUser.companyId },
						active: { eq: true },
					},
					limit: 1000,
					nextToken: null,
				},
				fetchPolicy: 'cache-and-network',
			}),
			props(props) {
				const userGroupsNextToken = get(
					props,
					'data.listUserGroups.nextToken',
					null
				);
				const onFetchMoreUserGroup = makeOnFetchMoreUserGroups(
					props.data.fetchMore,
					userGroupsNextToken
				);
				if (
					!props.data.loading &&
					(!props.data.listUserGroups || props.data.error)
				) {
					setTimeout(props.data.refetch, 2000);
					return null;
				}

				let userGroups = get(props, 'data.listUserGroups.items', []);
				if (userGroups.length > 0) {
					userGroups = userGroups.map((group) => {
						const newGroup = {...group};
						if (get(group, 'keywords')) newGroup.keywords = parse(group.keywords);
						return newGroup;
					});
				}

				if (userGroupsNextToken) onFetchMoreUserGroup();
				return {
					userGroups,
				};
			},
		}),
		graphql(gql(createUserGroup), {
			props: (props) => ({
				onCreateUserGroup(input) {
					const optimisticResponseData = {
						...input.input,
						id: uuid(),
						__typename: 'UserGroup',
					};
					props.mutate({
						variables: input,
						optimisticResponse: {
							__typeName: 'Mutation',
							createUserGroup: {
								__typename: 'createUserGroup',
								...optimisticResponseData,
							},
						},
						update(proxy, { data: { createUserGroup } }) {
							const data = proxy.readQuery({
								query: gql(listUserGroups),
								variables: {
									filter: {
										companyId: { eq: props.ownProps.company.id },
										active: { eq: true },
									},
									limit: 1000,
									nextToken: null,
								},
							});

							const itemExists = data.listUserGroups.items.find(
								(userGroup) => userGroup.id === createUserGroup.id
							);

							if (!itemExists) {
								const newData = {
									...data,
									listUserGroups: {
										...data.listUserGroups,
										items: [...data.listUserGroups.items, createUserGroup]
									}
								};

								proxy.writeQuery({
									query: gql(listUserGroups),
									data: newData,
									variables: {
										filter: {
											companyId: { eq: props.ownProps.company.id },
											active: { eq: true },
										},
										limit: 1000,
										nextToken: null,
									},
								});
							}
						}
					});
				},
			}),
		}),
		graphql(gql(updateUserGroup), {
			props: (props) => ({
				onUpdateUserGroup(input) {
					let existingUserGroup = null;
					try {
						const data = props.ownProps.userGroups || [];
						existingUserGroup = data.find(group => group.id === input.input.id);
					} catch (error) {
						console.warn('Could not find existing user group for optimistic response');
					}

					const optimisticResponseData = existingUserGroup
						? {
							...existingUserGroup,
							...input.input,
							__typename: 'UserGroup',
							languageCode: existingUserGroup.languageCode || null
						}
						: {
							...input.input,
							__typename: 'UserGroup',
							languageCode: null
						};

					props.mutate({
						variables: input,
						optimisticResponse: {
							__typename: 'Mutation',
							updateUserGroup: optimisticResponseData,
						},
						update(proxy, { data: { updateUserGroup } }) {
							try {
								const data = proxy.readQuery({
									query: gql(listUserGroups),
									variables: {
										filter: {
											companyId: { eq: props.ownProps.currentUser.companyId },
											active: { eq: true },
										},
										limit: 1000,
										nextToken: null,
									},
								});

								const updatedItems = data.listUserGroups.items.map(userGroup =>
									userGroup.id === updateUserGroup.id
										? { ...userGroup, ...updateUserGroup }
										: userGroup
								);

								const newData = {
									...data,
									listUserGroups: {
										...data.listUserGroups,
										items: updatedItems
									}
								};

								proxy.writeQuery({
									query: gql(listUserGroups),
									data: newData,
									variables: {
										filter: {
											companyId: { eq: props.ownProps.currentUser.companyId },
											active: { eq: true },
										},
										limit: 1000,
										nextToken: null,
									},
								});
							} catch (error) {
								console.warn('Cache update failed:', error);
							}
						}
					});
				},
			}),
		})
	)(Component);
};

const makeOnFetchMoreUserGroups = (fetchMore, nextToken) => {
	if (!nextToken) {
		return null;
	}

	return () => {
		fetchMore({
			variables: { nextToken },
			updateQuery(previous, { fetchMoreResult }) {
				if (!fetchMoreResult) {
					return previous;
				}

				const mergedItems = [
					...previous.listUserGroups.items,
					...fetchMoreResult.listUserGroups.items,
				];
				const set = new Set();
				const uniqueItems = mergedItems.filter((item) => {
					if (!set.has(item.id)) {
						set.add(item.id);
						return true;
					}

					return false;
				});
				return {
					...previous,
					listUserGroups: {
						...previous.listUserGroups,
						...fetchMoreResult.listUserGroups,
						items: uniqueItems,
					},
				};
			},
		});
	};
};